import axios, { AxiosResponse } from 'axios';
import * as https from 'https';
import _ from 'lodash/fp';
import { CustomersSettingsFetchByMode } from '../blocs/CustomersBloc/CustomersState';

class WooCommerceService {

    //static BASE_URL: string = 'https://local.webshop.corpotex.de/wp-json/cptx/v1/';
    static BASE_URL: string = 'https://webshop.corpotex.de/wp-json/cptx/v1/';

    /**
     * Load article data from WeClapp.
     *
     * @param articleNumber
     * @return API-Response
     */
    loadArticle = async ( articleNumber: string ) => {
        let params = `sku=${articleNumber}`;

        let result: AxiosResponse = await axios.get(
            WooCommerceService.getProxyEndpointUrl( `products?${params}` ),
            {
                httpsAgent: new https.Agent( {
                    rejectUnauthorized: false,
                } ),
            } );
        return result?.data;
    };

    postProduct = async ( wooCommerceData: any ) => {
        let result: AxiosResponse = await axios.post(
            WooCommerceService.getProxyEndpointUrl( `products` ),
            wooCommerceData,
            {
                httpsAgent: new https.Agent( {
                    rejectUnauthorized: false,
                } ),
            } );

        return result?.data;
    };

    updateProduct = async ( id: string, wooCommerceData: any ) => {
        let result: AxiosResponse = await axios.post(
            WooCommerceService.getProxyEndpointUrl( `products/${id}` ),
            wooCommerceData,
            {
                httpsAgent: new https.Agent( {
                    rejectUnauthorized: false,
                } ),
            } );

        return result?.data;
    };

    deleteProduct = async(id: string) => {
        let result: AxiosResponse = await axios.delete(
            WooCommerceService.getProxyEndpointUrl( `products/${id}` ),
            {
                httpsAgent: new https.Agent( {
                    rejectUnauthorized: false,
                } ),
            } );

        return result?.data;
    }

    postVariantArticle = async ( variantArticleId: string ) => {
        let params = `id=${variantArticleId}&full=1`;


        let result: AxiosResponse = await axios.get(
            WooCommerceService.getCptxEndpointUrl( `utils/importVariantArticle?${params}` ),
            {
                httpsAgent: new https.Agent( {
                    rejectUnauthorized: false,
                } ),
            } );

        return result?.data;
    };


    importImagesForArticle = async ( articleNumber: string ) => {
        let params                = `sku=${articleNumber}`;
        let result: AxiosResponse = await axios.get(
            WooCommerceService.getCptxEndpointUrl( `images/importImageFromWeClapp?${params}` ),
            {
                httpsAgent: new https.Agent( {
                    rejectUnauthorized: false,
                } ),
            },
        );

        return result?.data;
    };

    importImagesForBrandArticle = async ( productId: string, brand: string, update_existing: boolean = false ) => {
        let params                = `productId=${productId}&brand=${brand}&update=${update_existing}`;
        let result: AxiosResponse = await axios.get(
            WooCommerceService.getCptxEndpointUrl( `images/importBrandProductImages?${params}` ),
            {
                httpsAgent: new https.Agent( {
                    rejectUnauthorized: false,
                } ),
            },
        );

        return result?.data;
    };

    updateGenderAttributes = async () => {
        let result: AxiosResponse = await axios.get(
            WooCommerceService.getCptxEndpointUrl( `utils/importGenderTerms` ),
            {
                httpsAgent: new https.Agent( {
                    rejectUnauthorized: false,
                } ),
            },
        );

        return result?.data;
    };

    updateVariantAttributeOptions = async () => {
        let result: AxiosResponse = await axios.get(
            WooCommerceService.getCptxEndpointUrl( `utils/loadVariantAttributeOptions` ),
            {
                httpsAgent: new https.Agent( {
                    rejectUnauthorized: false,
                } ),
            },
        );

        return result?.data;
    };

    updateBrandAttributes = async () => {
        let result: AxiosResponse = await axios.get(
            WooCommerceService.getCptxEndpointUrl( `utils/importBrandCategories` ),
            {
                httpsAgent: new https.Agent( {
                    rejectUnauthorized: false,
                } ),
            },
        );

        return result?.data?.terms;
    };

    loadCustomer = async ( customerNumber: string, fetchBy: CustomersSettingsFetchByMode ) => {
        let param = `customerNumber=${customerNumber}`;

        if ( fetchBy == "id" ) {
            param = `id=${customerNumber}`;
        }

        let result: AxiosResponse = await axios.get(
            WooCommerceService.getCptxEndpointUrl( `webshop/customer?${param}` ),
            {
                httpsAgent: new https.Agent( {
                    rejectUnauthorized: false,
                } ),
            },
        );

        return result?.data;
    };

    importCustomer = async ( customerNumber: string, fetchBy: CustomersSettingsFetchByMode ) => {
        let param = `customerNumber=${customerNumber}`;

        if ( fetchBy == "id" ) {
            param = `id=${customerNumber}`;
        }

        let result: AxiosResponse = await axios.get(
            WooCommerceService.getCptxEndpointUrl( `utils/importCustomer?${param}` ),
            {
                httpsAgent: new https.Agent( {
                    rejectUnauthorized: false,
                } ),
            },
        );

        return result?.data;
    };

    updateCustomer = async ( customerNumber: string, fetchBy: CustomersSettingsFetchByMode ) => {
        let param = `customerNumber=${customerNumber}`;

        if ( fetchBy == "id" ) {
            param = `id=${customerNumber}`;
        }

        let result: AxiosResponse = await axios.get(
            WooCommerceService.getCptxEndpointUrl( `utils/updateCustomer?${param}` ),
            {
                httpsAgent: new https.Agent( {
                    rejectUnauthorized: false,
                } ),
            },
        );

        return result?.data;
    };

    static transformWeClappToWooCommerceData = ( weClappData: any ) => {
        let wooCommerceData: any;

        /**
         * Auto detect what type of weClappData should be transformed:
         * (based on articleNumber/variantArticleNumber property present on object).
         * Choose method to transform based on data type.
         */

        // Transform single article
        if ( !_.isUndefined( weClappData.articleNumber ) ) {
            wooCommerceData = WooCommerceService.transformSingleArticleToWcProduct( weClappData );
        }

        // Transform variantArticle
        if ( !_.isUndefined( weClappData.variantArticleNumber ) ) {
            wooCommerceData = WooCommerceService.transformVariantArticleToWcProduct( weClappData );
        }

        return wooCommerceData;
    };

    static transformVariantArticleToWcProduct = ( weClappData: any ) => {
        return {
            'status':            'publish',
            'sku':               weClappData?.variantArticleNumber ?? '',
            'name':              'Product',
            'short_description': '',
            'description':       '',
            'dimensions':        {
                "length": "",
                "width":  "",
                "height": "",
            },
            'dimensions.height': null,
            'dimensions.width':  null,
            'dimensions.length': null,
            'categories':        [],
            'type':              'variable', // 'simple' | 'variable'
            'weight':            null,
            // 'attributes':        $product_attribute,
            'variations':      [], // List of variation product ids
            'sync_variations': true,
        };
    };

    static transformSingleArticleToWcProduct = ( weClappData: any ) => {
        return {
            'status':            'publish',
            'sku':               weClappData?.articleNumber ?? '',
            'name':              weClappData?.name ?? 'Product',
            'short_description': '',
            'description':       weClappData?.description ?? '',
            'dimensions':        {
                "length": "",
                "width":  "",
                "height": "",
            },
            'dimensions.height': null,
            'dimensions.width':  null,
            'dimensions.length': null,
            'categories':        [],
            'type':              'simple', // 'simple' | 'variable'
            'weight':            weClappData?.articleNetWeight ?? null,
            // 'attributes':        $product_attribute,
            'variations': null, // List of variation product ids
        };
    };

    static getProxyEndpointUrl = ( endpoint: string ) => {
        return `${WooCommerceService.BASE_URL}woocommerce/proxy/${endpoint}`;
    };

    static getCptxEndpointUrl = ( endpoint: string ) => {
        return WooCommerceService.BASE_URL + endpoint;
    };
}

export default WooCommerceService;
